import React, { useState, useMemo } from 'react';
import { SavedPlayer, UpdateSavedPlayerData } from '@shared/types';
import { useSavedPlayers } from '../../hooks/useSavedPlayers';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface SavedPlayersManagerProps {
  roomId: string;
  onClose: () => void;
}

export function SavedPlayersManager({ roomId, onClose }: SavedPlayersManagerProps) {
  const { savedPlayers, loading, error, updatePlayer, deletePlayer } = useSavedPlayers(roomId);
  const [editingPlayer, setEditingPlayer] = useState<SavedPlayer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Filter players based on search query
  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return savedPlayers;
    
    const query = searchQuery.toLowerCase().trim();
    const digitsOnly = searchQuery.replace(/\D/g, '');
    
    return savedPlayers.filter(player => {
      // Name match (case-insensitive contains)
      if (player.name.toLowerCase().includes(query)) {
        return true;
      }
      // Phone match - only if search query has digits and player has phone
      if (digitsOnly.length > 0 && player.phone) {
        const playerDigits = player.phone.replace(/\D/g, '');
        return playerDigits.includes(digitsOnly);
      }
      return false;
    });
  }, [savedPlayers, searchQuery]);

  const handleDeletePlayer = async (playerId: string, playerName: string) => {
    if (!confirm(`Are you sure you want to delete "${playerName}" from saved players?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);
      await deletePlayer(playerId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete player');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Manage Saved Players</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or phone..."
            className="input"
          />
        </div>

        {actionError && (
          <div className="mb-4 rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-800">{actionError}</p>
          </div>
        )}

        {/* Players List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Error loading saved players: {error.message}</p>
            </div>
          ) : filteredPlayers.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-500">
                {searchQuery ? 'No players match your search' : 'No saved players yet'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Players are saved when you check "Save player for future visits" when adding to waitlist
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPlayers.map((player) => (
                <div 
                  key={player.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">
                        {player.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{player.name}</p>
                      {player.phone ? (
                        <p className="text-sm text-gray-500">{player.phone}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No phone</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingPlayer(player)}
                      className="btn btn-outline btn-sm"
                      disabled={actionLoading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePlayer(player.id, player.name)}
                      className="btn btn-danger btn-sm"
                      disabled={actionLoading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with count */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {filteredPlayers.length} {filteredPlayers.length === 1 ? 'player' : 'players'}
            {searchQuery && savedPlayers.length !== filteredPlayers.length && (
              <span> (filtered from {savedPlayers.length})</span>
            )}
          </p>
          <button onClick={onClose} className="btn btn-outline">
            Close
          </button>
        </div>

        {/* Edit Player Modal */}
        {editingPlayer && (
          <EditSavedPlayerModal
            player={editingPlayer}
            onSave={async (data) => {
              try {
                setActionLoading(true);
                setActionError(null);
                await updatePlayer(editingPlayer.id, data);
                setEditingPlayer(null);
              } catch (err) {
                setActionError(err instanceof Error ? err.message : 'Failed to update player');
              } finally {
                setActionLoading(false);
              }
            }}
            onCancel={() => setEditingPlayer(null)}
            loading={actionLoading}
          />
        )}
      </div>
    </div>
  );
}

// Edit Saved Player Modal
interface EditSavedPlayerModalProps {
  player: SavedPlayer;
  onSave: (data: UpdateSavedPlayerData) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

function EditSavedPlayerModal({ player, onSave, onCancel, loading }: EditSavedPlayerModalProps) {
  const [formData, setFormData] = useState<UpdateSavedPlayerData>({
    name: player.name,
    phone: player.phone || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Saved Player</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Player Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="Enter player name"
              required
            />
          </div>

          <div>
            <label className="label">Phone Number</label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              placeholder="Enter phone number (optional)"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

